import { faHandshake } from "@fortawesome/pro-duotone-svg-icons";
import { faExternalLinkAlt } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { tv } from "tailwind-variants";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";

// Copy of SidebarLink styles
const styles = tv({
  slots: {
    base: "group flex max-h-[74px] w-full flex-row items-center gap-30 border-t border-primary px-30 py-15 transition-colors hover:bg-gray-100",
    text: "transition-opacity duration-300 group-hover:line-clamp-1 group-hover:opacity-100",
    icon: "h-20",
    subtitle:
      "text-xs text-secondary opacity-0 transition-opacity duration-300 group-hover:opacity-100",
  },
  variants: {
    isActive: {
      true: { base: "bg-secondary-light" },
    },
    isSidebarExpanded: {
      true: {
        text: "line-clamp-1 truncate opacity-100",
        subtitle: "opacity-100",
      },
      false: {
        text: "line-clamp-1 opacity-0",
      },
    },
    isHeader: {
      true: {
        base: "bg-white",
        text: "text-secondary",
        icon: "text-secondary",
      },
    },
  },
});

// Copy of SidebarLink interface
interface HeaderLinkProps {
  icon: IconProp;
  title: string;
  url: string;
  isExternal?: boolean;
  isNewTab?: boolean;
  isActive: boolean;
  isSidebarExpanded: boolean;
  isHeader?: boolean;
  subtitle?: string;
}

// Copy of SidebarLink component
const HeaderLink = ({
  icon,
  title,
  url,
  isExternal,
  isNewTab,
  isActive,
  isSidebarExpanded,
  isHeader = true, // Default to true for header
  subtitle,
}: HeaderLinkProps) => {
  const {
    base,
    text,
    icon: iconStyles,
    subtitle: subtitleStyles,
  } = styles({ isActive, isSidebarExpanded, isHeader });

  const renderItemChildren = () => (
    <>
      <FontAwesomeIcon fixedWidth icon={icon} className={iconStyles()} />
      <div className="flex flex-col">
        <span className={text()}>
          {title}
          {isExternal && isNewTab && (
            <FontAwesomeIcon
              fixedWidth
              icon={faExternalLinkAlt}
              className="ml-10"
            />
          )}
        </span>
        {subtitle && <span className={subtitleStyles()}>{subtitle}</span>}
      </div>
    </>
  );

  if (isExternal) {
    return (
      <a
        href={url}
        target={isNewTab ? "_blank" : "_self"}
        className={base()}
        data-testid="sidebar-menu-link">
        {renderItemChildren()}
      </a>
    );
  }

  return (
    <Link to={url} className={base()} data-testid="sidebar-menu-link">
      {renderItemChildren()}
    </Link>
  );
};

interface SidebarHeaderProps {
  isSidebarExpanded: boolean;
}

const SidebarHeader = ({ isSidebarExpanded }: SidebarHeaderProps) => {
  const { onboardingStatus } = useOnboardingStatus();

  if (!onboardingStatus || !onboardingStatus.isOnboarding) return null;

  const { url, completedSteps, totalSteps } = onboardingStatus;
  const subtitle = `${completedSteps}/${totalSteps} completed`;

  return (
    <HeaderLink
      icon={faHandshake}
      title={"Get Started"}
      url={url}
      isActive={false}
      isSidebarExpanded={isSidebarExpanded}
      isHeader={true}
      subtitle={subtitle}
    />
  );
};

export default SidebarHeader;
